/**
 * Cost Calculator Service
 * 
 * Handles all checkout cost calculations including subtotal, shipping, tax,
 * discounts, and drop ship charges.
 * 
 * Follows Single Responsibility Principle - only concerned with cost calculations.
 */

/**
 * Retrieves numeric value from an element, defaulting to 0 if not found
 * @param {string} selector - CSS selector for the element
 * @param {string} property - Property to read ('value' for inputs, 'textContent' for text)
 * @returns {number} The numeric value
 */
function getNumericValue(selector, property = 'value') {
  const element = document.querySelector(selector)
  if (!element) return 0
  
  const rawValue = property === 'value' ? element.value : element.textContent
  return parseFloat(rawValue?.replace(/[^0-9.-]/g, '')) || 0
}

/**
 * Determines if a collect account shipping term is selected
 * @returns {boolean} True if collect account is selected
 */
export function isCollectAccountSelected() {
  const shippingTerms = document.querySelector('.shipping-terms :checked')
  return ['C', '3'].includes(shippingTerms?.value)
}

/**
 * Determines if a freight forwarder is selected
 * @returns {boolean} True if freight forwarder is selected
 */
export function isFreightForwarderSelected() {
  return document.querySelectorAll('.freight-forwarders :checked').length > 0
}

/**
 * Gets the selected shipping rate price
 * @returns {number} The shipping cost
 */
export function getShippingCost() {
  if (isCollectAccountSelected()) {
    return 0
  }
  
  const selectedOption = document.querySelector('.shipping-options :checked')
  return parseFloat(selectedOption?.dataset.ratePrice) || 0
}

/**
 * Calculates the complete checkout costs
 * @returns {CheckoutCosts} Object containing all cost components
 */
export function calculateCheckoutCosts() {
  const subtotal = getNumericValue('#subtotal')
  const tax = getNumericValue('#tax')
  const miscAmount = getNumericValue('#miscamt')
  const specialDiscount = getNumericValue('#special_discount')
  const dropShipCharge = getNumericValue('.dropshipcost', 'textContent')
  const shippingCost = getShippingCost()
  
  const total = shippingCost + (subtotal - specialDiscount) + tax + miscAmount + dropShipCharge
  
  return {
    subtotal,
    tax,
    miscAmount,
    specialDiscount,
    dropShipCharge,
    shippingCost,
    total,
    isCollectAccount: isCollectAccountSelected(),
    hasFreightForwarder: isFreightForwarderSelected()
  }
}

/**
 * Formats a number as currency string
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string (e.g., "$123.45")
 */
export function formatCurrency(amount) {
  return '$' + parseFloat(amount).toFixed(2)
}
